import { useUserStore } from '@/store/modules/user'
import { GlobalStore } from '@/store'
import progress from '@/config/nprogress'
import { WhiteListPage } from '@/enum/httpEnum'
import { Router, RouteRecordName, RouteRecordRaw } from 'vue-router'
import { usePermissionsStore } from '@/store/modules/user/permissions'
// import { Menu } from "@/types/Menu";
import { toRaw } from 'vue'
import { useNavTabStore } from '@/store/modules/tabs'
import { getAccessToken, clearAuthTokens } from '@/auth/jwt'

const DYN_MARK = '__DYNAMIC_ROUTES_LOADED__'

/**
 * 过滤 keep-alive 路由
 */
const filterKeepAlive = (routes: RouteRecordRaw[], cache: RouteRecordName[]) => {
  routes.forEach((route) => {
    if (route.meta?.keepalive && route.name) cache.push(route.name)
    if (route.children?.length) filterKeepAlive(route.children, cache)
  })
}

const guard = (router: Router) => {
  const whiteList = [WhiteListPage.LOGIN_PATH, WhiteListPage.NOT_FOUND_PATH, WhiteListPage.BI, WhiteListPage.BIVIEW]

  router.beforeEach(async (to, from, next) => {
    progress.start()

    const globalStore = GlobalStore()
    const token = await getAccessToken()

    const userStore = useUserStore()
    const permissionStore = usePermissionsStore()
    const navTabs = useNavTabStore()

    /** 未登录 */
    if (!token) {
      if (whiteList.includes(to.path as WhiteListPage)) {
        next()
      } else {
        next({ path: WhiteListPage.LOGIN_PATH })
      }
      progress.done()
      return
    }

    /** 已登录：禁止回登录页 */
    if (to.path === WhiteListPage.LOGIN_PATH) {
      next({ path: '/' })
      progress.done()
      return
    }

    /** 已登录访问 /404 直接放行，防止死循环 */
    if (to.path === WhiteListPage.NOT_FOUND_PATH) {
      next()
      progress.done()
      return
    }

    /** Tab */
    if (to.name && to.name !== 'not-found') {
      navTabs.addTabs({
        name: to.name as string,
        fullPath: to.fullPath,
        path: to.path,
        is_active: true,
        meta: {
          title: (to.meta.title as string) || '未命名',
          affix: (to.meta.affix as boolean) || false,
          icon: (to.meta.icon as string) || ''
        }
      })
    }

    /**
     * ② 首次登录 / 刷新无用户态
     */
    try {
      // 不要用 pinia 标记，改用 router marker
      if (router.hasRoute(DYN_MARK)) {
        next()
        return
      }

      // 尝试获取用户信息（接口可能不可用，不阻断导航）
      try {
        await userStore.getUserInfo()
      } catch (e) {
        console.warn('获取用户信息失败，跳过权限加载:', e)
      }

      const perms = toRaw(userStore.getPermissions || [])

      if (!perms.length) {
        console.warn('权限为空，无法生成动态路由')
        next()
        return
      }

      // 同步到 permissionStore
      permissionStore.permissions = perms

      // 生成 asyncMenus
      const asyncMenus = permissionStore.getAsyncMenusFrom(perms, true)

      // 恢复 component
      permissionStore.restoreComponents(asyncMenus)

      // 动态挂载
      asyncMenus.forEach((route) => {
        router.addRoute(route as RouteRecordRaw)
      })

      // 404 catch-all
      if (!router.hasRoute('not-found')) {
        router.addRoute({
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          meta: { title: '404' },
          component: () => import('@/components/404/index.vue')
        })
      }

      // 加 marker
      if (!router.hasRoute(DYN_MARK)) {
        router.addRoute({
          path: '/__dyn_loaded__',
          name: DYN_MARK,
          meta: { hidden: true },
          redirect: '/404'
        })
      }

      // keep-alive
      const cacheRouter: RouteRecordName[] = []
      filterKeepAlive(router.getRoutes(), cacheRouter)
      permissionStore.setCacheRouter(cacheRouter as string[])

      // 动态路由加完后，重新解析一次当前地址
      const resolved = router.resolve(to.fullPath)
      if (!resolved.matched.length) {
        // 这时才说明确实没有这个路由，再去 404
        next({ path: WhiteListPage.NOT_FOUND_PATH, replace: true })
        return
      }

      // 有匹配，回到原目标（关键：replace 一次让它重新走匹配）
      next({ ...to, replace: true })
      return
    } catch (err) {
      console.error('Permission Guard Error:', err)
      await clearAuthTokens()
      globalStore.removeToken()
      next({ path: WhiteListPage.LOGIN_PATH })
    } finally {
      progress.done()
    }
  })

  router.afterEach(() => {
    progress.done()
  })
}

export default guard
